import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { api } from '../lib/api'
import AddJobModal from '../components/AddJobModal'
import EditJobModal from '../components/EditJobModal'
import JobCard from '../components/JobCard'
import Settings from './Settings'
import Billing from './Billing'
import { BillingSuccess, BillingCancel } from './BillingPages'
import GettingStarted from './GettingStarted'

export default function Dashboard({ user, business, onBusinessUpdate }) {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddJob, setShowAddJob] = useState(false)
  const [editingJob, setEditingJob] = useState(null)
  const [filter, setFilter] = useState('upcoming')
  const [page, setPage] = useState('jobs')
  const [menuOpen, setMenuOpen] = useState(false)
  // Handle billing redirect pages
const pathname = window.location.pathname
if (pathname === '/billing/success') return <BillingSuccess />
if (pathname === '/billing/cancel') return <BillingCancel />

  const loadJobs = useCallback(async () => {
    try {
      const data = await api.getJobs(business.id)
      setJobs(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [business.id])

  useEffect(() => { loadJobs() }, [loadJobs])

  async function handleComplete(jobId) {
    await api.completeJob(jobId)
    loadJobs()
  }

  async function handleDelete(jobId) {
    await api.deleteJob(jobId)
    loadJobs()
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

  function handleJobAdded() {
    setShowAddJob(false)
    loadJobs()
  }

  function handleJobEdited() {
    setEditingJob(null)
    loadJobs()
  }

  function navTo(p) {
    setPage(p)
    setMenuOpen(false)
  }

  const now = new Date()
  const filteredJobs = jobs.filter(job => {
    const apptTime = new Date(job.appointment_time)
    if (filter === 'upcoming') return apptTime >= now && job.status !== 'complete'
    if (filter === 'complete') return job.status === 'complete'
    return true
  })

  const todayStart = new Date()
todayStart.setHours(0, 0, 0, 0)
const todayEnd = new Date()
todayEnd.setHours(23, 59, 59, 999)
const stats = {
  total: jobs.filter(j => {
    const t = new Date(j.appointment_time)
    return t >= todayStart && t <= todayEnd
  }).length,
    confirmed: jobs.filter(j => j.status === 'confirmed').length,
    pending: jobs.filter(j => j.status === 'pending' && new Date(j.appointment_time) >= now).length,
    noResponse: jobs.filter(j => j.no_show_alert_sent && j.status === 'pending').length,
  }

  return (
    <div style={s.root}>

      {/* Mobile header — hidden on desktop via CSS */}
      <header className="mobile-header" style={s.mobileHeader}>
        <div style={s.headerLeft}>
          <span style={s.logo}>⚡ JobConfirmed</span>
          <span style={s.bizName}>{business.name}</span>
        </div>
        <button style={s.menuBtn} onClick={() => setMenuOpen(!menuOpen)}>☰</button>
      </header>

      {menuOpen && (
        <div style={s.dropdown}>
          <button style={s.dropItem} onClick={() => navTo('jobs')}>📋 Jobs</button>
          <button style={s.dropItem} onClick={() => navTo('settings')}>⚙️ Settings</button>
          <button style={s.dropItem} onClick={() => navTo('billing')}>💳 Billing</button>
          <button style={s.dropItem} onClick={() => navTo('guide')}>📖 Getting Started</button>
          <button style={{...s.dropItem, color: '#ef4444'}} onClick={handleSignOut}>Sign out</button>
        </div>
      )}


      <div style={s.layout}>
        {/* Desktop sidebar — hidden on mobile via CSS */}
        <aside className="sidebar" style={s.sidebar}>
          <div style={s.sidebarLogo}>⚡ <span style={{marginLeft:6}}>JobConfirmed</span></div>
          <div style={s.sidebarBiz}>{business.name}</div>
          <nav style={s.nav}>
            <button style={{...s.navItem, ...(page==='jobs' ? s.navActive : {})}} onClick={() => setPage('jobs')}>📋 Jobs</button>
            <button style={{...s.navItem, ...(page==='settings' ? s.navActive : {})}} onClick={() => setPage('settings')}>⚙️ Settings</button>
            <button style={{...s.navItem, ...(page==='billing' ? s.navActive : {})}} onClick={() => setPage('billing')}>💳 Billing</button>
            <button style={{...s.navItem, ...(page==='guide' ? s.navActive : {})}} onClick={() => setPage('guide')}>📖 Getting Started</button>
          </nav>
          <button style={s.signOut} onClick={handleSignOut}>Sign out</button>
        </aside>

       <main style={s.main}>
  {page === 'settings' ? (
    <Settings business={business} onUpdate={onBusinessUpdate} />
  ) : page === 'billing' ? (
    <Billing user={user} business={business} />
  ) : page === 'guide' ? (
    <GettingStarted />
  ) : (
    <>
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.pageTitle}>Jobs</h1>
          <p style={s.pageDate}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <button style={s.addBtn} onClick={() => setShowAddJob(true)}>+ Add Job</button>
      </div>

              {/* Stats — 2 cols mobile, 4 cols desktop via CSS class */}
              <div className="stats-grid">
                <div style={s.statCard}><div style={s.statNum}>{stats.total}</div><div style={s.statLabel}>Today</div></div>
                <div style={s.statCard}><div style={{...s.statNum, color:'#22c55e'}}>{stats.confirmed}</div><div style={s.statLabel}>Confirmed</div></div>
                <div style={s.statCard}><div style={{...s.statNum, color:'#f59e0b'}}>{stats.pending}</div><div style={s.statLabel}>Pending</div></div>
                <div style={s.statCard}><div style={{...s.statNum, color:'#ef4444'}}>{stats.noResponse}</div><div style={s.statLabel}>No Response</div></div>
              </div>

              <div style={s.tabs}>
                {['upcoming','complete','all'].map(tab => (
                  <button key={tab} style={{...s.tab, ...(filter===tab ? s.tabActive : {})}} onClick={() => setFilter(tab)}>
                    {tab.charAt(0).toUpperCase()+tab.slice(1)}
                  </button>
                ))}
              </div>

              {loading ? (
                <div style={s.empty}>Loading...</div>
              ) : filteredJobs.length === 0 ? (
                <div style={s.empty}>
                  <div style={{fontSize:32}}>📋</div>
                  <div style={{color:'#6b6b80', marginTop:8}}>No jobs here. Add one to get started.</div>
                </div>
              ) : (
                <div style={s.jobList}>
                  {filteredJobs.map(job => (
                    <JobCard key={job.id} job={job} onComplete={handleComplete} onDelete={handleDelete} onEdit={setEditingJob} />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {showAddJob && <AddJobModal business={business} onClose={() => setShowAddJob(false)} onJobAdded={handleJobAdded} />}
      {editingJob && <EditJobModal job={editingJob} onClose={() => setEditingJob(null)} onJobEdited={handleJobEdited} />}
    </div>
  )
}

const s = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0a0f',
    color: '#fff',
    fontFamily: "'DM Sans', sans-serif",
  },
  mobileHeader: {
    justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', background: '#13131a', borderBottom: '1px solid #1e1e2e',
    position: 'sticky', top: 0, zIndex: 100,
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  logo: { fontSize: 18, fontWeight: 700, color: '#fff' },
  bizName: { fontSize: 11, color: '#6b6b80' },
  menuBtn: {
    background: 'none', border: '1px solid #2a2a3a', color: '#fff',
    borderRadius: 8, padding: '6px 14px', fontSize: 18, cursor: 'pointer',
  },
  dropdown: {
    background: '#13131a', borderBottom: '1px solid #1e1e2e',
    display: 'flex', flexDirection: 'column',
    position: 'sticky', top: 53, zIndex: 99,
  },
  dropItem: {
    background: 'none', border: 'none', color: '#fff',
    padding: '14px 20px', textAlign: 'left', fontSize: 15,
    cursor: 'pointer', borderBottom: '1px solid #1e1e2e',
  },
  // Fills whatever space is left under the header via flex:1, instead of
  // hardcoding `calc(100vh - 53px)`. That hardcoded value assumed the
  // mobile header's height always applies, but index.css hides it on
  // desktop (display:none at >=768px), leaving a 53px unstyled gap at the
  // bottom of the sidebar on desktop. flex:1 on a flex-column root fills
  // the remaining space correctly on both breakpoints automatically.
  layout: { display: 'flex', flex: 1 },
  sidebar: {
    width: 220, background: '#13131a', borderRight: '1px solid #1e1e2e',
    padding: '24px 16px', flexDirection: 'column', flexShrink: 0,
  },
  sidebarLogo: { fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4, display: 'flex', alignItems: 'center' },
  sidebarBiz: { fontSize: 12, color: '#6b6b80', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  navItem: {
    background: 'none', border: 'none', color: '#6b6b80',
    padding: '10px 12px', borderRadius: 8, textAlign: 'left',
    cursor: 'pointer', fontSize: 14, fontWeight: 500,
  },
  navActive: { background: '#1e1e2e', color: '#fff' },
  signOut: {
    background: 'none', border: 'none', color: '#4a4a60',
    fontSize: 13, cursor: 'pointer', textAlign: 'left', padding: '10px 12px',
  },
  main: { flex: 1, padding: '20px 16px', overflowY: 'auto', maxWidth: '100%' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  pageTitle: { fontSize: 24, fontWeight: 700, margin: 0 },
  pageDate: { color: '#6b6b80', fontSize: 13, margin: '4px 0 0' },
  addBtn: {
    background: '#4f46e5', color: '#fff', border: 'none',
    borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  statCard: { background: '#13131a', border: '1px solid #1e1e2e', borderRadius: 12, padding: '16px' },
  statNum: { fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 },
  statLabel: { color: '#6b6b80', fontSize: 12, marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: {
    background: 'none', border: '1px solid #1e1e2e', color: '#6b6b80',
    padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500,
  },
  tabActive: { background: '#1e1e2e', color: '#fff', borderColor: '#2a2a3a' },
  jobList: { display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { textAlign: 'center', padding: '60px 0', color: '#6b6b80' },
}
